import { WireTypes } from "@flakiness/server/common/wireTypes.js";
import { markdown } from "../markdown.js";
import { ciTabs } from "./welcome_ci_tabs.js";
import { otherCITabs } from "./welcome_other_ci.js";

const ghaOIDCInstructions = (project: WireTypes.Project) => markdown`
  Enable the \`id-token: write\` permission, run tests with the \`ci\` profile, then convert and upload the resulting \`junit.xml\`. GitHub Actions ships with \`npx\`, so \`@flakiness/junit-xml\` runs without a separate install step:

  ${markdown.yaml(`
    permissions:
      contents: read # Required for actions/checkout
      id-token: write # Required for GitHub OIDC

    steps:
      - uses: actions/checkout@v4
      - name: Run tests
        run: cargo nextest run --profile ci
      - name: Convert and upload to Flakiness.io
        if: always()
        run: npx @flakiness/junit-xml ./target/nextest/ci/junit.xml --category rust --flakiness-project ${project.org.orgSlug}/${project.projectSlug}
  `, `.github/workflows/tests.yml`, { ins: [[1, 3], [7, 11]] })}
`;

const gitlabOIDCInstructions = (project: WireTypes.Project) => markdown`
  GitLab mints the ID token when the job starts, so declare one named \`FLAKINESS_ID_TOKEN\` with your project as its audience. Rust images ship no Node.js, so install the standalone \`flakiness-junit-xml\` binary in \`after_script\`, which executes even when tests fail:

  ${markdown.yaml(`
    test:
      id_tokens:
        FLAKINESS_ID_TOKEN:
          aud: ${project.org.orgSlug}/${project.projectSlug} # Required for GitLab OIDC
      script:
        - cargo nextest run --profile ci
      after_script:
        - curl -fsSL https://github.com/flakiness/junit-xml/releases/latest/download/install.sh | sh
        - flakiness-junit-xml ./target/nextest/ci/junit.xml --category rust --flakiness-project ${project.org.orgSlug}/${project.projectSlug}
  `, `.gitlab-ci.yml`, { ins: [[2, 4], [7, 9]] })}
`;

export const welcome_rust = (project: WireTypes.Project) => {
  const otherCi = otherCITabs({
    idPrefix: 'rust',
    project,
    testCommand: 'cargo nextest run --profile ci',
    junitPath: './target/nextest/ci/junit.xml',
    category: 'rust',
  });
  return markdown`

Rust doesn't have a native Flakiness reporter. The simplest path is [\`cargo-nextest\`](https://nexte.st/), a drop-in \`cargo test\` replacement that emits JUnit XML.

1. **Adopt \`cargo-nextest\`.** If your project doesn't already use it, install it locally and switch your test command from \`cargo test\` to \`cargo nextest run\`. See the [nextest installation guide](https://nexte.st/docs/installation/pre-built-binaries/) for the recommended install for your OS.

1. **Enable JUnit output** for the \`ci\` profile by adding to your \`.config/nextest.toml\`:

    ${markdown.toml(`
      [profile.ci.junit]
      path = "junit.xml"
    `, `.config/nextest.toml`)}

    Running \`cargo nextest run --profile ci\` now writes JUnit XML to \`target/nextest/ci/junit.xml\`.

1. Configure your CI
    ${ciTabs({
      idPrefix: 'rust',
      project,
      githubActions: ghaOIDCInstructions(project),
      gitlabCI: gitlabOIDCInstructions(project),
      otherCI: otherCi,
    })}
`;
};
